import React, { useEffect, useState } from "react";
import API_URL from "../../services/api";
import "./Announcement.css";

const Announcement = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    announcement_id: "",
    title: "",
    description: "",
    announcement_date: "",
    status: "Active",
  });

  // ============================
  // FETCH ANNOUNCEMENTS
  // ============================

  const fetchAnnouncements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements`);
      const data = await res.json();

      setAnnouncements(data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  // ============================
  // HANDLE INPUT
  // ============================

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  // ============================
  // RESET FORM
  // ============================

  const resetForm = () => {
    setEditingId(null);

    setForm({
      announcement_id: "",
      title: "",
      description: "",
      announcement_date: "",
      status: "Active",
    });
  };

  // ============================
  // SAVE / UPDATE
  // ============================

  const saveAnnouncement = async () => {
    if (
      !form.announcement_id ||
      !form.title ||
      !form.description ||
      !form.announcement_date
    ) {
      alert("Please fill all fields.");
      return;
    }

    try {
      let url = `${API_URL}/api/announcements`;
      let method = "POST";

      if (editingId) {
        url = `${API_URL}/api/announcements/${editingId}`;
        method = "PUT";
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Operation failed.");
        return;
      }

      alert(
        data.message ||
          (editingId
            ? "Announcement updated successfully."
            : "Announcement added successfully.")
      );

      resetForm();
      fetchAnnouncements();
    } catch (err) {
      console.log(err);
      alert("Server Error");
    }
  };

  // ============================
  // EDIT
  // ============================

  const editAnnouncement = (item) => {
    setEditingId(item.id);

    setForm({
      announcement_id: item.announcement_id || "",
      title: item.title || "",
      description: item.description || "",
      announcement_date:
        item.announcement_date?.split("T")[0] || "",
      status: item.status || "Active",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // ============================
  // DELETE
  // ============================

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) {
      return;
    }

    try {
      const res = await fetch(
        `${API_URL}/api/announcements/${id}`,
        {
          method: "DELETE",
        }
      );

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Delete failed.");
        return;
      }

      alert(data.message || "Announcement deleted.");

      fetchAnnouncements();
    } catch (err) {
      console.log(err);
      alert("Server Error");
    }
  };

  // ============================
  // SEARCH
  // ============================

  const filteredAnnouncements = announcements.filter((item) => {
    const announcementId =
      item.announcement_id?.toLowerCase() || "";

    const title = item.title?.toLowerCase() || "";

    const description =
      item.description?.toLowerCase() || "";

    const status = item.status?.toLowerCase() || "";

    const searchValue = search.toLowerCase();

    return (
      announcementId.includes(searchValue) ||
      title.includes(searchValue) ||
      description.includes(searchValue) ||
      status.includes(searchValue)
    );
  });

  // ============================
  // STATISTICS
  // ============================

  const totalAnnouncements = announcements.length;

  const active = announcements.filter(
    (item) => item.status === "Active"
  ).length;

  const inactive = announcements.filter(
    (item) => item.status === "Inactive"
  ).length;

  // ============================
  // CSV DOWNLOAD
  // ============================

  const downloadCSV = () => {
    const headers = [
      "Announcement ID",
      "Title",
      "Description",
      "Date",
      "Status",
    ];

    const rows = filteredAnnouncements.map((item) => [
      item.announcement_id,
      item.title,
      item.description,
      item.announcement_date,
      item.status,
    ]);

    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((value) =>
            `"${String(value ?? "").replace(/"/g, '""')}"`
          )
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "Announcements.csv";

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="announcement-container">

      {/* ============================
              HEADER
      ============================ */}

      <div className="announcement-header">
        <div>
          <h1>Announcement Management</h1>
          <p>
            Create, manage and publish library announcements.
          </p>
        </div>
      </div>

      {/* ============================
              STATISTICS
      ============================ */}

      <div className="announcement-stats">

        <div className="announcement-stat-card total">
          <div className="stat-icon">📢</div>

          <div>
            <h2>{totalAnnouncements}</h2>
            <p>Total Announcements</p>
          </div>
        </div>

        <div className="announcement-stat-card active">
          <div className="stat-icon">✓</div>

          <div>
            <h2>{active}</h2>
            <p>Active</p>
          </div>
        </div>

        <div className="announcement-stat-card inactive">
          <div className="stat-icon">○</div>

          <div>
            <h2>{inactive}</h2>
            <p>Inactive</p>
          </div>
        </div>

      </div>

      {/* ============================
              FORM
      ============================ */}

      <div className="announcement-form-card">

        <div className="form-header">
          <div>
            <h2>
              {editingId
                ? "Edit Announcement"
                : "Create Announcement"}
            </h2>

            <p>
              {editingId
                ? "Update the selected announcement."
                : "Add a new announcement for library users."}
            </p>
          </div>

          {editingId && (
            <button
              className="cancel-button"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          )}
        </div>

        <div className="announcement-form">

          <div className="form-group">
            <label>Announcement ID</label>

            <input
              placeholder="e.g. ANN-001"
              name="announcement_id"
              value={form.announcement_id}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Announcement Title</label>

            <input
              placeholder="Enter announcement title"
              name="title"
              value={form.title}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Announcement Date</label>

            <input
              type="date"
              name="announcement_date"
              value={form.announcement_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Status</label>

            <select
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="form-group full-width">
            <label>Description</label>

            <textarea
              placeholder="Write announcement description..."
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </div>

        </div>

        <div className="form-actions">

          <button
            className="save-button"
            onClick={saveAnnouncement}
          >
            {editingId
              ? "Update Announcement"
              : "Add Announcement"}
          </button>

          {editingId && (
            <button
              className="secondary-button"
              onClick={resetForm}
            >
              Clear
            </button>
          )}

        </div>

      </div>

      {/* ============================
              SEARCH BAR
      ============================ */}

      <div className="announcement-toolbar">

        <div className="search-wrapper">
          <span>🔍</span>

          <input
            placeholder="Search announcements..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
          />
        </div>

        <button
          className="csv-button"
          onClick={downloadCSV}
        >
          <span>⬇</span>
          Download CSV
        </button>

      </div>

      {/* ============================
              DESKTOP TABLE
      ============================ */}

      <div className="announcement-table-container">

        <table className="announcement-table">

          <thead>
            <tr>
              <th>Announcement ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>

            {filteredAnnouncements.length === 0 ? (

              <tr>
                <td
                  colSpan="6"
                  className="no-data"
                >
                  <div>
                    <span>📢</span>
                    <p>No announcements found.</p>
                  </div>
                </td>
              </tr>

            ) : (

              filteredAnnouncements.map((item) => (

                <tr key={item.id}>

                  <td>
                    <strong>
                      {item.announcement_id}
                    </strong>
                  </td>

                  <td>
                    <strong>
                      {item.title}
                    </strong>
                  </td>

                  <td className="description-cell">
                    {item.description}
                  </td>

                  <td>
                    {new Date(
                      item.announcement_date
                    ).toLocaleDateString()}
                  </td>

                  <td>
                    <span
                      className={`status-badge ${
                        item.status === "Active"
                          ? "status-active"
                          : "status-inactive"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>

                  <td>

                    <div className="action-buttons">

                      <button
                        className="edit-button"
                        onClick={() =>
                          editAnnouncement(item)
                        }
                      >
                        Edit
                      </button>

                      <button
                        className="delete-button"
                        onClick={() =>
                          deleteAnnouncement(item.id)
                        }
                      >
                        Delete
                      </button>

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

      {/* ============================
              MOBILE CARDS
      ============================ */}

      <div className="announcement-mobile-list">

        {filteredAnnouncements.length === 0 ? (

          <div className="mobile-no-data">
            <span>📢</span>
            <h3>No announcements found</h3>
          </div>

        ) : (

          filteredAnnouncements.map((item) => (

            <div
              className="announcement-mobile-card"
              key={item.id}
            >

              <div className="mobile-card-header">

                <div>
                  <span className="mobile-id-label">
                    Announcement ID
                  </span>

                  <strong>
                    {item.announcement_id}
                  </strong>
                </div>

                <span
                  className={`status-badge ${
                    item.status === "Active"
                      ? "status-active"
                      : "status-inactive"
                  }`}
                >
                  {item.status}
                </span>

              </div>

              <h3>
                {item.title}
              </h3>

              <p className="mobile-description">
                {item.description}
              </p>

              <div className="mobile-date">
                📅{" "}
                {new Date(
                  item.announcement_date
                ).toLocaleDateString()}
              </div>

              <div className="mobile-actions">

                <button
                  className="edit-button"
                  onClick={() =>
                    editAnnouncement(item)
                  }
                >
                  Edit
                </button>

                <button
                  className="delete-button"
                  onClick={() =>
                    deleteAnnouncement(item.id)
                  }
                >
                  Delete
                </button>

              </div>

            </div>

          ))

        )}

      </div>

    </div>
  );
};

export default Announcement;
